import React from "react";
import {
  DefaultButton,
  PrimaryButton,
  Stack,
  TextField,
} from "@fluentui/react";
import { Formik, FormikErrors } from "formik";
import {
  addSLANotes,
  updateSLANotes,
} from "../../../services/sla-matrix/SlaMatrixService";
import { ISLANotes } from "../../../vm";
import {
  childrenGap,
  FILE_UPLOAD_CONFIG,
  FILE_UPLOAD_MODULES,
} from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import { mdiCloseCircle } from "@mdi/js";
import { THEME } from "../../../utils/Styles";
import Icon from "@mdi/react";
import {
  getFileByBase64,
  getFileTypeErrorMessage,
  uploadFile,
  validateFileType,
} from "../../../services/UtilService";
import ImageCropper from "../cctv-image/ImageCropper";

const slaNotesSchema = yup.object().shape({
  image: yup.string().optional(),
  remarks: yup
    .string()
    .optional()
    .max(720, "Remarks should be less than 720 characters"),
});

export interface ManageSlaNotesDialogProps {
  data?: ISLANotes;
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
}

var crop = {
  unit: "%",
  width: 100,
  aspect: 19 / 9,
};

const ManageSlaNotesDialog: React.FC<ManageSlaNotesDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  customerId,
}) => {
  //state
  const [state, setState] = React.useState({
    slaNotes: {
      image: "",
      remarks: "",
      customerId: customerId,
      deletedImage: "",
      ...data,
    },
    isLoading: false,
    selectedImage: undefined,
    manageImageCropDialog: {
      isOpen: false,
      data: undefined,
    },
  } as {
    slaNotes: ISLANotes;
    isLoading: boolean;
    selectedImage?: File;
    manageImageCropDialog: {
      isOpen: boolean;
      data?: any;
    };
  });

  console.log("data", data);

  //hooks
  const { showToast } = React.useContext(ToastContext);

  // file upload config
  const fileUploadConfig =
    FILE_UPLOAD_CONFIG[FILE_UPLOAD_MODULES.MANAGE_SLA_NOTES];

  const closeAddDialog = (data?: ISLANotes) => {
    handleDialogClose(data);
  };

  const handleFileChange = (files: any) => {
    if (files && files.length > 0) {
      // Validate file type
      const isValidType = validateFileType(
        files[0],
        fileUploadConfig.acceptedFileTypes
      );

      if (!isValidType) {
        showToast(
          getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription),
          "error"
        );
      }
      console.log("f", files[0]);

      if (isValidType) {
        setState({
          ...state,
          selectedImage: files[0],
          manageImageCropDialog: { isOpen: true, data: files[0] },
        });
      } else {
        setState({
          ...state,
          selectedImage: files[0],
        });
      }
    } else {
      showToast("Select only images", "error");
    }
  };

  const onCloseImageCropDialog = async (data: any) => {
    if (data) {
      let image = await getFileByBase64(data, "newFile.jpeg");
      setState({
        ...state,
        manageImageCropDialog: { isOpen: false, data: undefined },
        selectedImage: image,
      });
    } else {
      setState({
        ...state,
        manageImageCropDialog: { isOpen: false, data: undefined },
      });
    }
  };

  const uploadImage = async (fileName: string, file: File) => {
    handleLoading();
    let result = await uploadFile(file);
    handleLoading();
    if (result.success) {
      return result?.data?.fileName || "";
    } else {
      showToast(result.message || `Error while uploading image`, "error");
    }
    return undefined;
  };

  const deleteImage = (deletedImage: string, values: any) => {
    setState({
      ...state,
      slaNotes: {
        ...values,
        deletedImage,
        imageUrl: "",
      },
    });
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={state.slaNotes._id ? "Update SLA Notes" : "Add SLA Notes"}
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.slaNotes}
            validationSchema={slaNotesSchema}
            validate={(values: ISLANotes) => {
              const errors: FormikErrors<ISLANotes> = {};
              if (state.selectedImage) {
                const isValidType = validateFileType(
                  state.selectedImage,
                  fileUploadConfig.acceptedFileTypes
                );

                if (!isValidType) {
                  errors.image = getFileTypeErrorMessage(
                    fileUploadConfig.fileTypeDescription
                  );
                }
              }
              return errors;
            }}
            onSubmit={async (values: ISLANotes, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              if (state.selectedImage) {
                let name =
                  customerId +
                  "-" +
                  new Date().getTime() +
                  "-" +
                  state.selectedImage.name.toLowerCase().replace(/ /g, "");
                let fileName = await uploadImage(name, state.selectedImage);
                if (fileName) {
                  obj.image = fileName;
                } else return;
              }
              handleLoading();
              if (state.slaNotes._id) {
                result = await updateSLANotes(obj);
              } else {
                result = await addSLANotes(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
              } else {
                showToast(
                  result?.message ||
                    `Error while ${
                      state.slaNotes._id ? "updating" : "adding"
                    } sla notes`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              handleSubmit,
              setFieldValue,
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap} className="">
                  <Stack.Item>
                    <div className="ms-Grid p-0">
                      <div className="ms-Grid-row">
                        {values.imageUrl ? (
                          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg4 ms-xl4 ms-xxl4 c-height">
                            <img
                              className="w-100"
                              src={values.imageUrl}
                              alt="log"
                            />
                            <span
                              className="img-delete-icon"
                              onClick={() => deleteImage(values.image, values)}
                            >
                              <Icon
                                path={mdiCloseCircle}
                                size={0.8}
                                className="pointer"
                                color={THEME.palette.themePrimary}
                              />
                            </span>
                          </div>
                        ) : (
                          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12 ms-xxl12 custom-textfield">
                            <TextField
                              name="image"
                              type="file"
                              accept={fileUploadConfig.acceptedFileTypes}
                              onChange={(event: any) =>
                                handleFileChange(event.target.files)
                              }
                              onBlur={handleBlur}
                              //   value={values.image}
                              label="Image"
                              errorMessage={
                                errors.image && touched.image
                                  ? errors.image
                                  : ""
                              }
                              description={fileUploadConfig.hintText}
                              className="file-selector"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField
                      name={"remarks"}
                      label={"Remarks"}
                      multiline
                      description={`No of characters ${values.remarks.length} out of 720`}
                      autoAdjustHeight
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.slaNotes._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
      {state.manageImageCropDialog.isOpen && (
        <ImageCropper
          data={state.manageImageCropDialog.data}
          onClose={(data?: any) => onCloseImageCropDialog(data)}
          crop={crop}
        />
      )}
    </React.Fragment>
  );
};

export default ManageSlaNotesDialog;
